import React from "react";
import { Field, FieldArray, ErrorMessage } from "formik";
import { Plus, Trash2 } from "lucide-react";
import { Plan } from "../dataTypes";

interface RebillingTabProps {
  values: Plan;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  onPrevious: () => void;
  isSubmitting: boolean;
  isEditing: boolean;
}

const RebillingTab: React.FC<RebillingTabProps> = ({
  values,
  setFieldValue,
  onPrevious,
  isSubmitting,
  isEditing,
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Rebilling Configuration
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Configure additional rebilling options for overage charges or extra services.
        </p>

        <FieldArray name="rebilling">
          {({ push, remove }) => (
            <div className="space-y-4">
              <ErrorMessage name="rebilling" component="div" className="text-red-500 text-sm mb-4" />
              
              {values.rebilling.map((rebill, index) => (
                <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-xl p-6 bg-gray-50 dark:bg-gray-700/50">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Interval */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Billing Interval *
                      </label>
                      <Field
                        as="select"
                        name={`rebilling.${index}.interval`}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                      >
                        <option value="month">Monthly</option>
                        <option value="year">Yearly</option>
                      </Field>
                      <ErrorMessage name={`rebilling.${index}.interval`} component="div" className="text-red-500 text-sm mt-2" />
                    </div>

                    {/* Amount */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Amount *
                      </label>
                      <Field
                        name={`rebilling.${index}.amount`}
                        type="number"
                        step="0.01"
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                        placeholder="0.00"
                      />
                      <ErrorMessage name={`rebilling.${index}.amount`} component="div" className="text-red-500 text-sm mt-2" />
                    </div>

                    {/* Active & Remove */}
                    <div className="flex items-end gap-4">
                      <div className="flex-1">
                        <label className="flex items-center h-12">
                          <Field
                            type="checkbox"
                            name={`rebilling.${index}.active`}
                            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                          />
                          <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">Active</span>
                        </label>
                      </div>
                      
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="p-3 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() => push({
                  interval: "month",
                  amount: 0,
                  active: true,
                })}
                className="flex items-center gap-3 px-6 py-4 border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-xl hover:border-gray-400 dark:hover:border-gray-500 transition-colors w-full justify-center"
              >
                <Plus className="h-5 w-5" />
                Add Rebilling Option
              </button>
            </div>
          )}
        </FieldArray>
      </div>

      {/* Final Navigation & Submit */}
      <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onPrevious}
          className="px-8 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
        >
          Back
        </button>
        
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-8 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded-xl transition-colors duration-200 shadow-sm hover:shadow-md disabled:cursor-not-allowed flex items-center gap-3"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              {isEditing ? "Updating..." : "Creating..."}
            </>
          ) : (
            <>
              <span>{isEditing ? "Update Plan" : "Create Plan"}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default RebillingTab;